/**
 * Página de listado de citas CRM
 * @module pages/CRMAppointments
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { AppointmentList } from '@/components/appointments/AppointmentList';
import { AppointmentForm } from '@/components/appointments/AppointmentForm';
import { AppointmentFilters, type AppointmentFiltersState } from '@/components/appointments/AppointmentFilters';
import { ICalExportButton } from '@/components/appointments/ICalExportButton';
import { ICalImportButton } from '@/components/appointments/ICalImportButton';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/common/Modal';
import { useAppointments } from '@/hooks/useAppointments';
import { useCRMUsers } from '@/hooks/useCRMUsers';
import type { AppointmentCreate } from '@/types/appointment.types';

const DEFAULT_APPOINTMENT_FILTERS: AppointmentFiltersState = {
  sort_by: 'scheduled_at',
  sort_order: 'desc',
};

export function CRMAppointments() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState<AppointmentFiltersState>(DEFAULT_APPOINTMENT_FILTERS);

  const { users: responsibleUsers, loading: loadingUsers } = useCRMUsers({ isActive: true, onlyResponsibles: true });
  const availableAgents = useMemo(
    () =>
      responsibleUsers.map((u) => ({
        id: u.id,
        name: u.name?.trim() || u.email?.trim() || 'Usuario sin nombre',
      })),
    [responsibleUsers]
  );

  const { create: createAppointment } = useAppointments(filters);

  const handleCreateAppointment = async (data: AppointmentCreate) => {
    try {
      await createAppointment(data);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creando cita:', error);
    }
  };

  const handleAppointmentClick = (appointmentId: string) => {
    navigate(`/crm/appointments/${appointmentId}`);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Citas</h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestiona las citas de tus oportunidades
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => navigate('/crm/calendar')}
              variant="outline"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Ver Calendario
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Nueva Cita
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros (estilo oportunidades/contactos) */}
      <div className="mb-6 space-y-3">
        <AppointmentFilters
          filters={filters}
          onFiltersChange={setFilters}
          availableAgents={availableAgents}
          loadingAgents={loadingUsers}
        />
        <div className="flex flex-wrap items-center gap-2">
          <ICalExportButton
            filters={{ delegation_id: filters.delegation_id }}
            variant="outline"
            size="sm"
          />
          <ICalImportButton
            params={{ delegation_id: filters.delegation_id }}
            variant="outline"
            size="sm"
          />
        </div>
      </div>

      {/* Lista de citas (ordenada por más recientes por defecto) */}
      <AppointmentList
        filters={filters}
        onAppointmentClick={handleAppointmentClick}
      />

      {/* Modal de crear cita */}
      {showCreateModal && (
        <Modal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Nueva Cita"
        >
          <AppointmentForm
            onSubmit={handleCreateAppointment}
            onCancel={() => setShowCreateModal(false)}
          />
        </Modal>
      )}
    </div>
  );
}
